import React, { useState } from 'react';
import { Button } from './ui/button';
import { Card } from './ui/card';
import { useHashPackWallet } from '../contexts/HashPackWalletContext';

interface WalletModalProps {
  isOpen: boolean;
  onClose: () => void;
}

declare global {
  interface Window {
    hashpack?: any;
  }
}

const WalletModal: React.FC<WalletModalProps> = ({ isOpen, onClose }) => {
  const { connect, loading, error } = useHashPackWallet();

  const walletOptions = [
    {
      id: 'hashpack',
      name: 'HashPack',
      icon: '📦',
      description: 'Connect with HashPack Hedera wallet',
      color: 'bg-green-500/20 border-green-500/30'
    }
  ];

  const handleWalletConnect = async (walletId: string) => {
    if (walletId === 'hashpack') {
      try {
        await connect();
        onClose();
      } catch (error) {
        console.error('Error connecting to HashPack:', error);
      }
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 animate-fade-in font-mono">
      <div className="bg-[#0d0d2a] border border-green-500/30 rounded-2xl p-8 max-w-md w-full mx-4 shadow-2xl animate-slide-up">
        <div className="text-center mb-8">
          {/* Logo */}
          <div className="flex justify-center mb-6">
            <img 
              src="/logo.png" 
              alt="COPYCAT Logo" 
              className="w-16 h-16 object-contain"
            />
          </div>
          <h2 className="text-2xl font-bold text-green-400 mb-2">
            Connect Wallet
          </h2>
          <p className="text-green-400/70">
            Choose your preferred wallet to connect to Copycat
          </p>
        </div>

        <div className="space-y-4">
          {walletOptions.map((wallet) => (
            <Card
              key={wallet.id}
              className="p-4 cursor-pointer bg-[#0a0a1a] border-green-500/30 hover:border-green-400/50 hover:bg-green-500/5 transition-all duration-200"
              onClick={() => handleWalletConnect(wallet.id)}
            >
              <div className="flex items-center space-x-4">
                <div className={`w-12 h-12 rounded-xl ${wallet.color} flex items-center justify-center text-green-400 text-xl border`}>
                  {wallet.icon}
                </div>
                <div className="flex-1">
                  <h3 className="font-semibold text-green-300">{wallet.name}</h3>
                  <p className="text-sm text-green-400/70">{wallet.description}</p>
                </div>
                {loading && wallet.id === 'hashpack' ? (
                  <div className="w-6 h-6 border-2 border-green-400 border-t-transparent rounded-full animate-spin"></div>
                ) : (
                  <div className="text-green-400">→</div>
                )}
              </div>
            </Card>
          ))}
        </div>

        {error && (
          <div className="mt-4 p-3 bg-red-500/10 border border-red-500/30 rounded-lg">
            <p className="text-red-400 text-sm">{error}</p>
          </div>
        )}

        <div className="mt-8 text-center">
          <Button
            variant="ghost"
            onClick={onClose}
            className="text-green-400/70 hover:text-green-400 border-green-500/30 hover:bg-green-500/10"
          >
            Cancel
          </Button>
        </div>

        <div className="mt-6 text-center">
          <p className="text-xs text-green-400/50">
            By connecting a wallet, you agree to our Terms of Service and Privacy Policy
          </p>
        </div>
      </div>
    </div>
  );
};

export default WalletModal;
